/**
 * Back in Stock notification modal.
 * Custom element <back-in-stock-modal> handles:
 * - Opening the modal with product context
 * - Sending subscription request to external API (Vercel + Supabase)
 * - Step transitions: form -> success/almost/error
 * - Close on overlay, button, Escape
 */

const BIS_API_URL = 'https://frostix-bis.vercel.app/api';

class BackInStockModal extends HTMLElement {
  constructor() {
    super();

    this.preventScrollKeys = (e) => {
      const scrollKeys = ['ArrowUp', 'ArrowDown', 'Space', 'PageUp', 'PageDown', 'Home', 'End'];
      if (scrollKeys.includes(e.code) || e.code === ' ') {
        e.preventDefault();
      }
    };
  }

  connectedCallback() {
    this.customerEmail = this.dataset.customer || '';
    this.isCustomer = this.customerEmail.length > 0;
    this.emailInput = this.querySelector('[data-bis-email]');
    this.submitBtn = this.querySelector('[data-bis-submit]');

    this.productTitle = '';
    this.productHandle = '';
    this.productUrl = '';

    this.querySelectorAll('[data-close]').forEach((el) => {
      el.addEventListener('click', (e) => {
        if (e.target === el || e.currentTarget === el) this.close();
      });
    });

    this.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') this.close();
    });

    if (this.submitBtn) {
      this.submitBtn.addEventListener('click', () => this.handleSubmit());
    }

    const retryBtn = this.querySelector('[data-bis-retry]');
    if (retryBtn) {
      retryBtn.addEventListener('click', () => this.showStep('form'));
    }

    if (this.emailInput) {
      this.emailInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleSubmit();
        }
      });
    }
  }

  open(productTitle, productHandle, productUrl) {
    this.productTitle = productTitle;
    this.productHandle = productHandle;
    this.productUrl = productUrl;

    this.showStep('form');
    if (this.emailInput) this.emailInput.value = '';

    this.setAttribute('open', '');

    this.preventScroll = (e) => { e.preventDefault(); };
    document.addEventListener('wheel', this.preventScroll, { passive: false });
    document.addEventListener('touchmove', this.preventScroll, { passive: false });
    document.addEventListener('keydown', this.preventScrollKeys, { passive: false });

    const focusTarget = this.emailInput || this.submitBtn;
    if (focusTarget) focusTarget.focus();
  }

  close() {
    this.removeAttribute('open');

    if (this.preventScroll) {
      document.removeEventListener('wheel', this.preventScroll);
      document.removeEventListener('touchmove', this.preventScroll);
      document.removeEventListener('keydown', this.preventScrollKeys);
    }

    this.classList.remove('loading');
    if (this.submitBtn) this.submitBtn.disabled = false;
  }

  showStep(stepName) {
    this.querySelectorAll('[data-step]').forEach((step) => {
      step.hidden = step.dataset.step !== stepName;
    });
  }

  async handleSubmit() {
    const email = this.isCustomer
      ? this.customerEmail
      : (this.emailInput ? this.emailInput.value.trim() : '');

    if (!email || !email.includes('@')) {
      if (this.emailInput) this.emailInput.focus();
      return;
    }

    if (this.submitBtn) this.submitBtn.disabled = true;
    this.classList.add('loading');

    try {
      const response = await fetch(`${BIS_API_URL}/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          productHandle: this.productHandle,
          productTitle: this.productTitle,
          productUrl: this.productUrl,
        }),
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        if (this.isCustomer || data.customerExists) {
          this.showStep('success');
        } else {
          this.showStep('almost');
        }
      } else {
        this.showStep('error');
      }
    } catch (error) {
      console.error('Back in Stock: submit error', error);
      this.showStep('error');
    } finally {
      this.classList.remove('loading');
      if (this.submitBtn) this.submitBtn.disabled = false;
    }
  }
}

customElements.define('back-in-stock-modal', BackInStockModal);

/**
 * Global click delegate for [data-back-in-stock] buttons.
 */
document.addEventListener('click', (e) => {
  const btn = e.target.closest('[data-back-in-stock]');
  if (!btn) return;

  e.preventDefault();
  e.stopPropagation();

  const modal = document.querySelector('#BackInStockModal');
  if (modal) {
    modal.open(
      btn.dataset.productTitle || '',
      btn.dataset.productHandle || '',
      btn.dataset.productUrl || ''
    );
  }
});
